/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Alert } from 'antd'
import { useCallback, useState, useEffect, useMemo } from 'react'
import useWindowSize from 'src/hooks/useWindowSize'
import Button from 'src/components/ts-components/Button'
import { TextAreaInput } from 'src/components/ts-components/TextAreaInput'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import BaseModal from 'src/components/ts-components/BaseModal'
import {
  ConditionType,
  RegisteredPlayer,
  Team,
} from '@sportninja/common/types/Registration'
import { RegistrationTableDivision } from 'src/pages/Schedule/PlayerRegistration/helpers'

type ApproveWithConditionsModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (params: {
    scheduleId: any
    scheduleRegistrationId: any
    status: any
    teamRosterId: any
    teamId: any
    conditions: any
    conditionType: any
    divisionId: any
    team_registration_id: any
    team: any
  }) => Promise<void>
  entity: Team | RegisteredPlayer | null
  divisionId: string | null
  scheduleId: string
  scheduleRegistrationId: string | null
  divisions: RegistrationTableDivision[]
  conditionType: ConditionType[]
  refetch?: (force?: boolean) => void
}

const conditionOptions = (
  divisionLength: number,
  conditionType: ConditionType[]
) => {
  let options: Array<{ label: string; value: string }> = []

  conditionType.forEach((item) => {
    if (Number(item.id) === 2 && divisionLength > 1) {
      options.push({
        label: item.name,
        value: item.id.toString(),
      })
    } else {
      options.push({
        label: item.name,
        value: item.id.toString(),
      })
    }
  })

  return [...options]
}

const ApproveWithConditionsModal = ({
  isOpen,
  onClose,
  onConfirm,
  entity,
  divisionId,
  scheduleId,
  scheduleRegistrationId,
  divisions,
  conditionType,
  refetch,
}: ApproveWithConditionsModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [comment, setCommentState] = useState('')
  const [selectedDivision, setSelectedDivisionState] = useState<string>('')
  const [status, setStatusState] = useState<string>('')

  // Enhanced setters to clear errors when user interacts with form
  const setComment = useCallback(
    (value: string) => {
      setCommentState(value)
      if (error) {
        setError(null) // Clear error when user starts typing
      }
    },
    [error]
  )

  const setSelectedDivision = useCallback(
    (value: string) => {
      setSelectedDivisionState(value)
      if (error) {
        setError(null) // Clear error when user changes division
      }
    },
    [error]
  )

  const setStatus = useCallback(
    (value: string) => {
      setStatusState(value)
      if (error) {
        setError(null) // Clear error when user changes status
      }
    },
    [error]
  )
  const [allDivisions, setAllDivisions] = useState<
    Array<{ label: string; value: string }>
  >([])
  const [formData, setFormData] = useState({
    fieldErrors: [] as string[],
    customError: {} as Record<string, string>,
  })

  console.log('🔄 ApproveWithConditionsModal: Rendering modal', {
    isOpen,
    entity,
    divisionId,
  })

  // Reset state when modal opens with new entity
  useEffect(() => {
    if (isOpen && entity) {
      console.log(
        '🔄 ApproveWithConditionsModal: Resetting state for new entity'
      )
      setCommentState('')
      setStatusState('')
      setSelectedDivisionState('')
      setAllDivisions([])
      setError(null) // Reset error state when modal opens
      setFormData({
        fieldErrors: [],
        customError: {},
      })

      // Build available divisions list (excluding current division)
      const availableDivisions: Array<{ label: string; value: string }> = []
      divisions
        ?.filter(
          (division) =>
            division?.id !== divisionId && division?.registration !== null
        )
        .forEach((division) => {
          availableDivisions.push({
            label: division.name,
            value: division.id,
          })
        })

      setAllDivisions(availableDivisions)
    }
  }, [isOpen, entity, divisionId, divisions])

  const setCustomError = useCallback((field: string, m: string) => {
    setFormData((prev) => ({
      ...prev,
      customError: {
        ...prev.customError,
        [field]: m,
      },
    }))
  }, [])

  const setFieldErrors = useCallback((field: string) => {
    setFormData((prev) => ({
      ...prev,
      fieldErrors: [...new Set([...prev.fieldErrors, field])],
    }))
  }, [])

  const setCustomErrorByField = useCallback(
    (field: string, m: string) => {
      setCustomError(field, m)
      setFieldErrors(field)
    },
    [setCustomError, setFieldErrors]
  )

  const confirmExit = useCallback(() => {
    handleClose()
  }, [])

  const handleClose = useCallback(() => {
    console.log('🔄 ApproveWithConditionsModal: Closing modal')
    setCommentState('')
    setStatusState('')
    setSelectedDivisionState('')
    setAllDivisions([])
    setError(null) // Reset error state when closing
    setFormData({
      fieldErrors: [],
      customError: {},
    })
    onClose()
  }, [onClose])

  const onRegStatusChange = useCallback(async () => {
    console.log('🔄 ApproveWithConditionsModal: Processing status change')
    setIsLoading(true)
    setError(null)

    try {
      // Validate required fields
      if (comment.length < 1) {
        setCustomErrorByField('comment', 'This field cannot be blank')
        setIsLoading(false) // Reset loading state on validation failure
        return
      }

      if (!status) {
        setCustomErrorByField('status', 'Please select a condition type')
        setIsLoading(false) // Reset loading state on validation failure
        return
      }

      // Validate division selection for condition type 2
      if (Number(status) === 2 && !selectedDivision) {
        setCustomErrorByField('selectedDivision', 'Please select a division')
        setIsLoading(false) // Reset loading state on validation failure
        return
      }

      const params = {
        scheduleId: scheduleId,
        scheduleRegistrationId: scheduleRegistrationId,
        status: 4, // Approved with conditions
        teamRosterId: (entity as Team)?.team_roster_id,
        teamId: (entity as any)?.uid || (entity as any)?.id,
        conditions: comment.trim(),
        conditionType: Number(status) === 1 ? 1 : 2,
        team_registration_id: (entity as Team)?.id,
        divisionId: Number(status) === 2 ? selectedDivision : null,
        team: entity as Team,
      }

      await onConfirm(params)

      // Always refetch on success to ensure data consistency
      if (refetch) {
        await refetch(true) // Use await to ensure refetch completes before closing
      }

      handleClose() // Only close after successful update and refetch
    } catch (e: any) {
      console.error('🚨 ApproveWithConditionsModal: Error updating status:', e)
      setError(e?.message || 'Failed to update status') // Set local error instead of closing
      // Do NOT call handleClose() here - keep modal open for retry
    } finally {
      setIsLoading(false)
    }
  }, [
    comment,
    status,
    selectedDivision,
    scheduleId,
    scheduleRegistrationId,
    entity,
    onConfirm,
    refetch,
    handleClose,
    setCustomErrorByField,
  ])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: ${isMobile ? '100%' : '50%'};
          margin-left: ${isMobile ? '0' : 'auto'};
          @media (max-width: 768px) {
            width: 100%;
            flex-direction: column;
          }
        `}
      >
        <Button text='CANCEL' onClick={confirmExit} variant='secondary' />
        <Button
          text={isLoading ? 'Processing...' : 'CONFIRM'}
          onClick={onRegStatusChange}
          variant='primary'
          disabled={
            isLoading ||
            comment.length < 1 ||
            !status ||
            (Number(status) === 2 && !selectedDivision)
          }
        />
      </div>
    )
  }, [
    isMobile,
    isLoading,
    comment.length,
    status,
    selectedDivision,
    onRegStatusChange,
    confirmExit,
  ])

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={confirmExit}
      title='Approve with Conditions'
      actions={getFooter()}
      desktopWidth='30vw'
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 20px;
          width: 100%;
        `}
      >
        {/* Condition Type Selection */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
          `}
        >
          <SelectInput
            label='Condition Type'
            disabled={false}
            options={conditionOptions(divisions.length, conditionType)}
            name='status'
            value={status}
            onChange={setStatus}
            fieldErrors={formData.fieldErrors}
            customError={formData.customError}
            placeholder='Select a condition type'
          />
        </div>

        {/* Division Selection (only show when condition type is 2) */}
        {status && Number(status) === 2 && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
              width: 100%;
            `}
          >
            <SelectInput
              label='Select the Division'
              disabled={false}
              options={allDivisions}
              name='selectedDivision'
              value={selectedDivision}
              onChange={setSelectedDivision}
              fieldErrors={formData.fieldErrors}
              customError={formData.customError}
              placeholder='Select a division'
            />
          </div>
        )}

        {/* Condition Description */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
          `}
        >
          <TextAreaInput
            name='comment'
            value={comment}
            setValue={setComment}
            fieldErrors={formData.fieldErrors}
            customError={formData.customError}
            label='Condition Description *'
            placeholder='Type the condition here...'
            maxLength={255}
            rows={3}
          />
        </div>
      </div>

      {error && <Alert message={error} type='error' showIcon />}
    </BaseModal>
  )
}

export default ApproveWithConditionsModal
